import React, { useState } from 'react';

import { Menu, MenuItem, Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { capitalCase } from 'change-case';
import { ConfirmationDialog, SelectButton, UserProfileDialog } from 'src/components';
import type { UsersListRow } from 'src/components/admin/users/UsersList';
import { useAccessGuard } from 'src/components/auth';
import { EditIcon } from 'src/components/icons/EditIcon';
import { OptionsMenuIcon } from 'src/components/icons/OptionsMenuIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { overflowMenuCSSId } from 'src/helpers';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';
import type { ReactSetStateAction } from 'src/types/general';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles<StyleProps>()((theme, { menuOpen }) => ({
  avatar: {
    marginRight: theme.spacing(2),
  },
  button: {
    borderRadius: 3,
    height: 32,
    marginLeft: theme.spacing(1),
    visibility: !menuOpen ? ('hidden' as const) : undefined,
    width: 32,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap' as const,
    justifyContent: 'space-between',
    width: '100%',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  menu: {
    width: 190,
  },
}));

interface StyleProps {
  menuOpen: boolean;
}

export function RoleCell({
  row: _row,
  value: user,
}: GridCellParams<UsersListRow, UsersListRow['role'], string>): JSX.Element | null {
  const dispatch = useNotificationDispatch();
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState<boolean>(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState<boolean>(false);
  const [updateUserActivation] = Apollo.useUpdateUserActivationMutation();
  const { classes } = useStyles({ menuOpen });

  async function flipUserActivationStatus(): Promise<void> {
    if (!user) return;

    const { data } = await updateUserActivation({
      variables: {
        isDeactivated: !user.isDeactivated,
        userId: user.id,
      },
    });
    if (!data) return;

    dispatch({
      payload: {
        key: uuid4(),
        message: `"${user.displayName}" successfully updated.`,
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  if (!user) return null;

  return (
    <div className={classes.container}>
      <Typography variant="body1">{capitalCase(user.role)}</Typography>
      <SelectButton
        ref={setAnchorElement}
        className={classes.button}
        id={overflowMenuCSSId}
        selected={menuOpen}
        // Capture the event in the capture cycle and prevent propagation so the click on the menu button doesn't select/de-select the associated row.
        onClickCapture={event => {
          event.stopPropagation();
          setMenuOpen(!menuOpen);
        }}
      >
        <OptionsMenuIcon />
      </SelectButton>
      <UserRoleMenu
        anchorElement={anchorElement}
        menuOpen={menuOpen}
        setDeactivateDialogOpen={setDeactivateDialogOpen}
        setMenuOpen={setMenuOpen}
        setUserDialogOpen={setUserDialogOpen}
        user={user}
      />
      <UserProfileDialog open={userDialogOpen} setOpen={setUserDialogOpen} user={user} />
      <ConfirmationDialog
        message={`Are you sure you want to ${user.isDeactivated ? 'reactivate' : 'deactivate'} "${user.displayName}"?`}
        open={deactivateDialogOpen}
        primaryText={user.isDeactivated ? 'Reactivate' : 'Deactivate'}
        title={user.isDeactivated ? 'Reactivate User' : 'Deactivate User'}
        onClickPrimary={flipUserActivationStatus}
        onClose={() => setDeactivateDialogOpen(false)}
      />
    </div>
  );
}

function UserRoleMenu({
  anchorElement,
  menuOpen,
  setDeactivateDialogOpen,
  setMenuOpen,
  setUserDialogOpen,
  user,
}: {
  anchorElement: HTMLButtonElement | null;
  menuOpen: boolean;
  setDeactivateDialogOpen: ReactSetStateAction<boolean>;
  setMenuOpen: ReactSetStateAction<boolean>;
  setUserDialogOpen: ReactSetStateAction<boolean>;
  user: Pick<Apollo.User, 'id' | 'role' | 'isDeactivated'>;
}): JSX.Element {
  const { classes } = useStyles({ menuOpen });
  const currentUser = useSelectCurrentUser();
  const handleClickEdit = useAccessGuard({
    callback: () => {
      setMenuOpen(false);
      setUserDialogOpen(true);
    },
    minRole: user.id !== currentUser?.id ? Apollo.OrgRole.Admin : undefined,
  });
  const handleClickDeactivate = useAccessGuard({
    callback: () => {
      setMenuOpen(false);
      setDeactivateDialogOpen(true);
    },
    minRole: Apollo.OrgRole.Admin,
  });

  return (
    <Menu
      anchorEl={anchorElement}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      classes={{ paper: classes.menu }}
      open={menuOpen}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      variant="menu"
      onClose={() => setMenuOpen(false)}
    >
      <MenuItem onClick={handleClickEdit}>
        <EditIcon className={classes.icon} />
        Edit
      </MenuItem>
      <MenuItem onClick={handleClickDeactivate}>
        <TrashcanIcon className={classes.icon} />
        {user.isDeactivated ? 'Reactivate' : 'Deactivate'}
      </MenuItem>
    </Menu>
  );
}
